import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { hasPermission, ModulePermissions, getRolePermissions } from '../types';
export function usePermissions() {
  const {
    user
  } = useAuth();
  const {
    isLoading,
    permissions: dbPermissions
  } = useData();
  const [permissions, setPermissions] = useState<ModulePermissions | null>(null);

  // Memoized function to reload permissions from localStorage
  const reloadPermissions = useCallback(() => {
    if (user) {
      const newPerms = getRolePermissions(user.role);
      setPermissions(newPerms);
    } else {
      setPermissions(null);
    }
  }, [user]);

  // CRITICAL: Re-read permissions when DataContext finishes loading
  // This is the PRIMARY mechanism - when Supabase data loads and syncs to localStorage,
  // this effect fires and reads the fresh permissions
  useEffect(() => {
    if (!isLoading && user) {
      reloadPermissions();
    }
  }, [isLoading, user, reloadPermissions]);

  // ALSO re-read when dbPermissions array changes (e.g., admin saves new permissions)
  useEffect(() => {
    if (user) {
      reloadPermissions();
    }
  }, [dbPermissions, user, reloadPermissions]);

  // ALSO listen for the explicit permissions-updated event (backup mechanism)
  useEffect(() => {
    const handlePermissionsUpdate = () => {
      reloadPermissions();
    };
    window.addEventListener('permissions-updated', handlePermissionsUpdate);
    return () => {
      window.removeEventListener('permissions-updated', handlePermissionsUpdate);
    };
  }, [reloadPermissions]);

  // Update permissions when user changes (login/logout)
  useEffect(() => {
    reloadPermissions();
  }, [user, reloadPermissions]);
  const canCreate = (module: keyof ModulePermissions) => {
    if (!user || !permissions) return false;
    return permissions[module] === 'full';
  };
  const canEdit = (module: keyof ModulePermissions) => {
    if (!user || !permissions) return false;
    return permissions[module] === 'full' || permissions[module] === 'own';
  };
  const canDelete = (module: keyof ModulePermissions) => {
    if (!user || !permissions) return false;
    return permissions[module] === 'full';
  };
  const canView = (module: keyof ModulePermissions) => {
    if (!user || !permissions) return false;
    const level = permissions[module];
    if (level === 'none') return false;
    if (level === 'full' || level === 'read' || level === 'own') return true;
    return false;
  };
  const isReadOnly = (module: keyof ModulePermissions) => {
    if (!user || !permissions) return true;
    return permissions[module] === 'read';
  };
  return {
    canCreate,
    canEdit,
    canDelete,
    canView,
    isReadOnly,
    permissions,
    user
  };
}